import React, { useMemo, useState } from "react";
import { IWindow } from "@/store/slices/windows.slice";
import { explorerItemsList } from "@/data/explorer.items.list";
import { setDNASampleLog } from "@/firebase/logs";
import { Div } from "@/components/base.styled";
import hourglass from "@/components/icons/hourglass.gif";
import ErrorDialog from "@/components/error.dialog";
import { SubmitButtonWithLoading } from "@/components/buttons";
import * as St from "./styles";

interface DNARegistryProps {
  activeWindow: IWindow;
}

const DNARegistry: React.FC<DNARegistryProps> = ({ activeWindow }) => {
  const [dnaSample, setDnaSample] = useState({
    prefix: "",
    postfix: "",
  });

  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>();

  const explorerItem = useMemo(() => {
    return explorerItemsList.getItemById(activeWindow.currentExplorer)!;
  }, [activeWindow.currentExplorer]);

  const dnaConfig = explorerItem.dna_config;
  const dnaSampleIndex = explorerItem.dna_sample_index || [];

  const submitHandler: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    const button = e.currentTarget;
    button.disabled = true;
    setError(undefined);
    setResult(null);

    if (dnaSample.prefix === "" || dnaSample.postfix === "") {
      button.disabled = false;
      setError("Bitte füllen Sie alle Felder aus.");
      return;
    }

    // Validate format
    if (dnaConfig) {
      if (dnaSample.prefix.length !== dnaConfig.prefix_digits) {
        button.disabled = false;
        setError(`Präfix muss ${dnaConfig.prefix_digits} Zeichen lang sein.`);
        return;
      }

      if (dnaSample.postfix.length !== dnaConfig.postfix_digits) {
        button.disabled = false;
        setError(`Postfix muss ${dnaConfig.postfix_digits} Ziffern lang sein.`);
        return;
      }
    }

    // Log the DNA sample lookup
    setDNASampleLog(`${dnaSample.prefix}-${dnaSample.postfix}`);

    // Simulate loading
    setTimeout(() => {
      // Look for exact match (case insensitive)
      const match = dnaSampleIndex.find(
        (entry) =>
          entry.prefix.toUpperCase() === dnaSample.prefix.toUpperCase() &&
          entry.postfix === dnaSample.postfix
      );

      if (match) {
        setResult(match.message);
      } else {
        setResult("Keine Übereinstimmung gefunden. Probe nicht in Datenbank.");
      }

      button.disabled = false;
    }, 800);
  };

  return (
    <St.DNARegistryWrapper>
      <div>
        <p>Geben Sie die DNA Proben-ID ein:</p>
        <St.InputsWrapper>
          <St.Input
            type="text"
            value={dnaSample.prefix}
            onChange={(e) =>
              setDnaSample((prev) => ({
                ...prev,
                prefix:
                  e.target.value.trim().length > (dnaConfig?.prefix_digits || 3)
                    ? prev.prefix
                    : e.target.value.trim().toUpperCase(),
              }))
            }
            placeholder="XXX"
          />
          <St.Separator>&mdash;</St.Separator>
          <St.Input
            css={{
              flexGrow: 1,
            }}
            type="text"
            value={dnaSample.postfix}
            onChange={(e) => {
              // Only allow numbers
              const value = e.target.value.trim().replace(/\D/g, "");
              setDnaSample((prev) => ({
                ...prev,
                postfix:
                  value.length > (dnaConfig?.postfix_digits || 8)
                    ? prev.postfix
                    : value,
              }));
            }}
            placeholder="12345678"
          />
          <SubmitButtonWithLoading onClick={submitHandler}>
            <span className="text">Suchen</span>
            <img src={hourglass} alt="hourglass" className="loader" />
          </SubmitButtonWithLoading>
        </St.InputsWrapper>
      </div>

      {/* Divider */}
      <Div
        css={{
          height: "0.2rem",
          backgroundColor: "$mono_white",
          margin: "$md 0",
        }}
      />

      {/* DNA Result */}
      <div>
        <p>Abfrage Ergebnis:</p>

        <St.ResultWrapper>
          <St.ResultTextBox>{result || ""}</St.ResultTextBox>
        </St.ResultWrapper>
      </div>

      {/* Error Dialog */}
      {error && (
        <ErrorDialog
          message={error}
          closeErrorDialog={() => setError(undefined)}
        />
      )}
    </St.DNARegistryWrapper>
  );
};

export default DNARegistry;

